import React, { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Sparkles } from "lucide-react";

// Editable, manager-only discussion suggestions (hidden from employees at the API layer).
export default function ConversationStarters({ calibrationId, initialStarters, ready }) {
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync when the AI finishes generating or parent refreshes
  useEffect(() => {
    if (!dirty) setItems(initialStarters || []);
  }, [initialStarters, dirty]);

  const upd = (i, v) => { setItems((xs) => xs.map((s, idx) => (idx === i ? v : s))); setDirty(true); };
  const rm = (i) => { setItems((xs) => xs.filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => { setItems((xs) => [...xs, ""]); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/calibrations/${calibrationId}/conversation-starters`, { starters: items });
      setItems(r.data.starters);
      setDirty(false);
      toast.success("Discussion points saved");
    } catch (err) { toast.error(apiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="cal-card p-8 mb-10" data-testid="conversation-starters">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] flex items-center gap-2">
            <Sparkles size={12} /> [ PRIVATE · MANAGER ONLY ]
          </div>
          <h2 className="font-display font-bold text-3xl mt-1">Discussion notes</h2>
          <p className="text-[#666] text-sm mt-1">
            AI-drafted starters you can rewrite, re-order, or replace. Only you can see and edit these — never shared with the employee through the app.
          </p>
        </div>
      </div>

      {!ready ? (
        <div className="font-mono-cal text-xs text-[#666] mt-5" data-testid="starters-generating">[ GENERATING AI SUGGESTIONS… AUTO-REFRESHES ]</div>
      ) : (
        <>
          <div className="space-y-3 mt-5">
            {items.length === 0 && (
              <div className="font-mono-cal text-xs text-[#666]">No notes yet. Add a talking point below.</div>
            )}
            {items.map((s, i) => (
              <div key={i} className="flex gap-3 items-start" data-testid={`starter-row-${i}`}>
                <span className="font-display font-bold text-2xl text-[#FF4F00] leading-none pt-2">·</span>
                <textarea
                  value={s}
                  onChange={(e) => upd(i, e.target.value)}
                  rows={2}
                  className="flex-1 p-3 border border-[#111] focus:ring-2 focus:ring-[#FF4F00] outline-none resize-none text-[15px] leading-relaxed"
                  data-testid={`starter-input-${i}`}
                />
                <button onClick={() => rm(i)} className="p-2 border border-[#111] hover:bg-[#111] hover:text-white transition"
                  title="Remove" data-testid={`starter-remove-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-5">
            <button onClick={add} className="cal-btn cal-btn-ghost" data-testid="starter-add">
              <Plus size={14} /> Add talking point
            </button>
            {dirty && (
              <button onClick={save} disabled={saving} className="cal-btn" data-testid="starter-save">
                <Save size={14} /> {saving ? "Saving…" : "Save changes"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
